import React, { useState } from 'react';
import { Copy, AlertTriangle, X } from 'lucide-react';
import { PRODUCT_FAMILIES, getProductFamilyMeta } from './ProductFamilyTabs';

/**
 * DuplicateToFamilyDialog
 *
 * A reusable modal for the "Duplicate to family" admin action.
 * Used on Sales Rate Cards (primary), BOM, and Flows.
 *
 * Props:
 *  - open: bool
 *  - onClose: () => void
 *  - sourceLabel: string (e.g., "Product Rate card 2026")
 *  - sourceFamily: string (vinyl|cd|dvd|bluray)
 *  - entityType: 'rate_card' | 'bom' | 'flow'
 *  - onConfirm: async (payload) => void
 *      payload = {
 *        product_family,
 *        new_name?,
 *        new_product_id?, // BOM only
 *        copy_items?, copy_components?, copy_steps?,
 *        copy_bom_references?, copy_supplier_skus?,
 *        cross_family_bom_policy?, cross_family_sku_policy?,
 *      }
 *  - busy: bool
 */
export default function DuplicateToFamilyDialog({
  open,
  onClose,
  sourceLabel,
  sourceFamily = 'vinyl',
  entityType,
  onConfirm,
  busy = false,
}) {
  const [targetFamily, setTargetFamily] = useState(
    PRODUCT_FAMILIES.find((f) => f.id !== sourceFamily)?.id || 'cd'
  );
  const [newName, setNewName] = useState('');
  const [newProductId, setNewProductId] = useState(''); // BOM only

  // Copy choices (defaults: header + items + linked references)
  const [copyMain, setCopyMain] = useState(true); // items / components / steps
  const [copyRefs, setCopyRefs] = useState(true); // bom refs / supplier skus
  const [crossFamilyPolicy, setCrossFamilyPolicy] = useState('review');

  React.useEffect(() => {
    if (!open) return;
    // Reset on open
    setTargetFamily(PRODUCT_FAMILIES.find((f) => f.id !== sourceFamily)?.id || 'cd');
    setNewName('');
    setNewProductId('');
    setCopyMain(true);
    setCopyRefs(true);
    setCrossFamilyPolicy('review');
  }, [open, sourceFamily]);

  if (!open) return null;

  const isCrossFamily = targetFamily !== sourceFamily;
  const targetMeta = getProductFamilyMeta(targetFamily);
  const TargetIcon = targetMeta.icon;

  const labelMap = {
    rate_card: { entity: 'rate card', main: 'price items', refs: 'BOM references' },
    bom: { entity: 'BOM', main: 'components', refs: 'supplier SKU links' },
    flow: { entity: 'flow', main: 'flow steps', refs: 'asset category references' },
  };
  const labels = labelMap[entityType] || labelMap.rate_card;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (busy) return;
    const payload = { product_family: targetFamily };
    if (newName.trim()) payload.new_name = newName.trim();
    if (entityType === 'rate_card') {
      payload.copy_items = copyMain;
      payload.copy_bom_references = copyMain && copyRefs;
      payload.cross_family_bom_policy = crossFamilyPolicy;
    } else if (entityType === 'bom') {
      if (!newProductId.trim()) {
        alert('New product ID is required');
        return;
      }
      payload.new_product_id = newProductId.trim();
      payload.copy_components = copyMain;
      payload.copy_supplier_skus = copyMain && copyRefs;
      payload.cross_family_sku_policy = crossFamilyPolicy;
    } else if (entityType === 'flow') {
      payload.copy_steps = copyMain;
    }
    onConfirm(payload);
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="duplicate-to-family-dialog"
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h3 className="font-semibold text-slate-900 flex items-center gap-2">
            <Copy className="w-4 h-4 text-indigo-600" />
            Duplicate {labels.entity} to family
          </h3>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="text-slate-400 hover:text-slate-600"
            data-testid="duplicate-to-family-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="text-sm text-slate-600">
            Source: <span className="font-medium text-slate-900">{sourceLabel}</span>{' '}
            <span className="text-xs px-1.5 py-0.5 bg-slate-100 rounded ml-1 uppercase">{sourceFamily}</span>
          </div>

          {/* Target family */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Target product family
            </label>
            <div className="grid grid-cols-4 gap-2">
              {PRODUCT_FAMILIES.map((fam) => {
                const Icon = fam.icon;
                const isActive = targetFamily === fam.id;
                return (
                  <button
                    key={fam.id}
                    type="button"
                    onClick={() => setTargetFamily(fam.id)}
                    data-testid={`duplicate-target-${fam.id}`}
                    className={`flex flex-col items-center gap-1 py-2 rounded-lg border-2 transition-all ${
                      isActive
                        ? 'border-indigo-500 bg-indigo-50 text-indigo-700'
                        : 'border-slate-200 hover:border-slate-300 text-slate-700'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-xs font-medium">{fam.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* New name */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              New name <span className="text-slate-400 font-normal">(optional)</span>
            </label>
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder={`${sourceLabel} (${targetMeta.label.toUpperCase()} copy)`}
              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
              data-testid="duplicate-new-name"
            />
          </div>

          {/* BOM-only: new product_id */}
          {entityType === 'bom' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                New product ID <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={newProductId}
                onChange={(e) => setNewProductId(e.target.value)}
                placeholder="e.g., cd-jewel-case-standard"
                required
                className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono"
                data-testid="duplicate-new-product-id"
              />
              <p className="text-xs text-slate-500 mt-1">
                Must be unique across all BOMs.
              </p>
            </div>
          )}

          {/* What to copy */}
          <div className="space-y-2 border-t border-slate-100 pt-4">
            <p className="text-sm font-medium text-slate-700">What to copy</p>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={copyMain}
                onChange={(e) => setCopyMain(e.target.checked)}
                className="mt-0.5"
                data-testid="duplicate-copy-main"
              />
              <span className="text-sm">
                <span className="font-medium">Copy {labels.main}</span>
                <span className="block text-xs text-slate-500">
                  Header is always copied. Uncheck to start with an empty {labels.entity}.
                </span>
              </span>
            </label>

            {entityType !== 'flow' && (
              <label className={`flex items-start gap-2 cursor-pointer ${!copyMain ? 'opacity-50' : ''}`}>
                <input
                  type="checkbox"
                  checked={copyRefs}
                  onChange={(e) => setCopyRefs(e.target.checked)}
                  disabled={!copyMain}
                  className="mt-0.5"
                  data-testid="duplicate-copy-refs"
                />
                <span className="text-sm">
                  <span className="font-medium">Copy linked {labels.refs}</span>
                  <span className="block text-xs text-slate-500">
                    When unchecked, copied {labels.main} are flagged "needs review" so you can re-link them in the target family.
                  </span>
                </span>
              </label>
            )}
          </div>

          {/* Cross-family warning + policy */}
          {isCrossFamily && copyMain && copyRefs && entityType !== 'flow' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-sm text-amber-900">
                  <p className="font-medium">Cross-family references detected</p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    {sourceFamily.toUpperCase()} {labels.refs} may not exist in{' '}
                    {targetMeta.label.toUpperCase()}. Choose how to handle them:
                  </p>
                </div>
              </div>
              <div className="space-y-1 pl-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="cross-family-policy"
                    value="review"
                    checked={crossFamilyPolicy === 'review'}
                    onChange={(e) => setCrossFamilyPolicy(e.target.value)}
                    data-testid="duplicate-policy-review"
                  />
                  <span className="text-xs">
                    <span className="font-medium">Keep & flag for review</span> (recommended)
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="cross-family-policy"
                    value="remap"
                    checked={crossFamilyPolicy === 'remap'}
                    onChange={(e) => setCrossFamilyPolicy(e.target.value)}
                    data-testid="duplicate-policy-remap"
                  />
                  <span className="text-xs">
                    <span className="font-medium">Remap if equivalent exists</span> in target family
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="cross-family-policy"
                    value="clear"
                    checked={crossFamilyPolicy === 'clear'}
                    onChange={(e) => setCrossFamilyPolicy(e.target.value)}
                    data-testid="duplicate-policy-clear"
                  />
                  <span className="text-xs">
                    <span className="font-medium">Clear references</span> — start clean
                  </span>
                </label>
              </div>
            </div>
          )}

          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="text-xs text-slate-500 flex items-center gap-2">
              <TargetIcon className="w-3.5 h-3.5" />
              New {labels.entity} will be saved as <span className="font-medium">draft / inactive</span>.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="px-4 py-2 border border-slate-300 rounded-lg text-sm hover:bg-slate-50 disabled:opacity-50"
                data-testid="duplicate-cancel"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={busy}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
                data-testid="duplicate-confirm"
              >
                {busy ? 'Duplicating...' : `Duplicate to ${targetMeta.label}`}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
